import AddIcon from '@mui/icons-material/Add';
import { Button, Dialog, DialogActions, DialogContent, Divider } from '@mui/material';
import PropTypes from 'prop-types';
import { useLayoutEffect, useMemo, useState } from 'react';

import { AddManualLotEnabled } from 'common/utils/featureFlags';

import ChangeNdcLotBeforeAfter from './ChangeNdcLotBeforeAfter';
import ChangeNdcLotBulkApply from './ChangeNdcLotBulkApply';
import ChangeNdcLotManualEntry from './ChangeNdcLotManualEntry';
import ChangeNdcLotDialogTitle, { findLotNeedingReplacement } from './ChangeNdcLotDialogTitle';
import ChangeNdcLotLotSection from './ChangeNdcLotLotSection';
import ChangeNdcLotManualSummary from './ChangeNdcLotManualSummary';
import ChangeNdcLotNdcCards from './ChangeNdcLotNdcCards';
import ChangeNdcLotShortfallNotice from './ChangeNdcLotShortfallNotice';
import ChangeNdcLotStockLots from './ChangeNdcLotStockLots';
import { withBatchShare } from '../utils/batchLotUsage';
import { buildStockLotSelection, isSameAsRecorded } from '../utils/bulkLotNdcStaging';
import { selectableLotsByNdc, shortfallAgainstFill } from '../utils/bulkLotNdcStatus';
import { describeLabelAfterApply, describeRecordedLabel } from '../utils/changeNdcLotPreview';
import { buildNdcCards, deriveManualEntry, isRecordableNdc } from '../utils/bulkManualLotEntry';
import useBulkApplyChoice from '../hooks/useBulkApplyChoice';
import { useForeignLotLookup } from '../hooks/useForeignLotLookup';
import { warningTextTone, warningTint } from '../utils/warningTone';

/**
 * POM-6010 — pick an NDC and a lot for one reviewed row.
 *
 * Issues NO network requests. Everything comes from the `availability` prop, which shipped with the
 * initial review query: the batch is edited against one authoritative snapshot rather than N
 * per-row fetches that could disagree with each other. The trade-off is that a dialog left open a
 * long time offers slightly stale counts — the commit re-validates and reports a refusal, and the
 * row stays editable so the user can pick again.
 *
 * v1 is single-lot: the chosen lot must cover the whole fill quantity on its own, so only lots that
 * can are offered and Apply carries exactly one allocation. `lotAllocations` is still shaped as a
 * list so a split editor can arrive without changing the mutation.
 */
const ChangeNdcLotDialog = ({
  open,
  item,
  availability,
  recordable,
  sessionManualLots,
  batchNdcClaims,
  batchLotShareFor,
  planBulkApplyFor,
  lotLookupCache,
  change,
  todayStr,
  onApply,
  onCancel,
}) => {
  const fillQuantity = Number(item?.fillQuantity ?? 0);
  // Memoised because `?? []` yields a new array identity on every render, which would defeat every
  // useMemo below it.
  const groups = useMemo(() => availability?.groups ?? [], [availability]);
  // Null for most partners. Everything keyed off it stays hidden in that case rather than
  // rendering an empty affordance.
  const preferredNdc = item?.partnerPreferredNdc ?? null;

  const [selectedNdc, setSelectedNdc] = useState(null);
  const [selectedLotId, setSelectedLotId] = useState(null);

  const clearLotSelection = () => {
    setSelectedLotId(null);
  };

  /**
   * POM-6121 — the hand-recorded claim, and the draft behind it.
   *
   * `manualSelection` is the committed entry the Apply button sends; the three fields above it are
   * the panel's working state. Held here rather than in the panel because Apply gates on the same
   * rules the panel renders, and one evaluation of them (deriveManualEntry) has to serve both.
   */
  const [manualOpen, setManualOpen] = useState(false);
  const [manualLotNumber, setManualLotNumber] = useState('');
  // POM-6179 — the re-key. Only ever required for a lot number the operator typed themselves; see
  // manualEntryError for why a lot taken from the list is not re-keyed.
  const [manualConfirmLotNumber, setManualConfirmLotNumber] = useState('');
  const [manualBud, setManualBud] = useState('');
  const [manualPickedLotId, setManualPickedLotId] = useState(null);
  const [manualSelection, setManualSelection] = useState(null);

  const {
    foreignNdcLots,
    sameNdcLotsOnFile,
    lookupFailed,
    isPending: lotCheckPending,
  } = useForeignLotLookup({
    // Not run for a lot taken from the list. That row IS this NDC's, so the only answer the
    // lookup could give is one that refuses a lot inventory itself holds here — and it would
    // make the operator wait out a debounce for it.
    enabled: manualOpen && !manualPickedLotId,
    lotNumber: manualLotNumber,
    ndc: selectedNdc,
    cache: lotLookupCache,
  });

  /**
   * The lots the row records today, for the CURRENT marker.
   *
   * `soleRecordedLotId` is the one that can be pre-selected and the one the no-op rule is about: a
   * multi-lot fill has no single "current" pick, because v1 replaces the whole allocation.
   */
  const recordedLots = useMemo(() => item?.recordedLots ?? [], [item]);
  const recordedLotIds = useMemo(
    () => new Set(recordedLots.map((lot) => lot.inventoryLotId)),
    [recordedLots]
  );
  const soleRecordedLotId = recordedLots.length === 1 ? recordedLots[0].inventoryLotId : null;

  /**
   * The closed inventory-master NDC list for this product and facility.
   *
   * Read in three places below — the seed, the manual-mode gate and the cards — so it is resolved
   * once, above the effect that needs it.
   */
  const ndcOptions = recordable?.ndcOptions ?? [];

  /** The panel's working fields. Kept in one place so a new field cannot be reset in three. */
  const clearManualDraft = () => {
    setManualLotNumber('');
    setManualConfirmLotNumber('');
    setManualBud('');
    setManualPickedLotId(null);
  };

  // POM-6121 — may a short lot be claimed here? The conditions hand-recording needs, minus the
  // inventory master: the shortfall lands on a lot inventory already holds, so a master row exists
  // by definition. Declared above usableByNdc because that map is built from it.
  const shortfallAllowed = AddManualLotEnabled && item?.canRecordUnverifiedLot === true;

  /**
   * POM-6121 — the lots this picker may offer.
   *
   * A lot holding LESS than the fill is offerable now: the operator sees what it holds, confirms
   * the remainder becomes an unverified claim, and the fill takes both out of the one lot.
   *
   * One map for everything downstream — NDC cards, the in-stock count and the lot list all read
   * it, so a lot offerable here cannot be described as unavailable two lines away.
   */
  // POM-6536 — what the rest of the batch leaves on each lot for this row.
  const batchLotShare = useMemo(() => batchLotShareFor?.(item) ?? null, [batchLotShareFor, item]);

  const usableByNdc = useMemo(
    () =>
      withBatchShare({
        lotsByNdc: selectableLotsByNdc({
          groups,
          fillQuantity,
          todayStr,
          allowShortfall: shortfallAllowed,
        }),
        shareByLotId: batchLotShare,
        fillQuantity,
        shortfallAllowed,
      }),
    [groups, fillQuantity, todayStr, shortfallAllowed, batchLotShare]
  );

  /**
   * Seed the picker with what this row would print right now: the uncommitted edit if it has one,
   * else the NDC and lot on the record.
   *
   * The lot is seeded, which it deliberately was not before. Opening with nothing selected on a
   * row whose lot is perfectly good gave the user nothing to recognise, so the natural move was to
   * click the lot already shown in the grid and Apply — which staged a no-op and badged the row
   * Changed. Pre-selecting it says "this is what you have" and leaves Apply with nothing to do
   * until something actually moves.
   *
   * Only ever seeds a lot that is on offer, so the expired-lot rows this dialog exists for still
   * open with no lot chosen: an unusable lot is filtered out of the list below, and pre-selecting
   * the problem would be the opposite of helpful.
   *
   * A layout effect, so the seed lands before the first paint: as a plain effect the dialog showed
   * one frame with nothing picked, then grew and jumped (QA, 2 Nov).
   */
  useLayoutEffect(() => {
    if (!open) return;

    // POM-6121: the manual panel opens closed and empty on every row. A draft left over from the
    // row edited before this one would offer a lot number for the wrong product.
    setManualOpen(false);
    clearManualDraft();
    // Reopening a row whose uncommitted edit IS a hand-recorded claim has to show that claim, not
    // an empty picker — otherwise the only way back to it is to type it again.
    setManualSelection((change?.lotAllocations ?? []).find((lot) => lot.isManualEntry) ?? null);

    const [pendingLot] = change?.lotAllocations ?? [];
    const seedNdc = change?.ndc ?? item?.effectiveNdc ?? null;
    /* Offered by EITHER list. An NDC that exists only in the inventory master has no stock and so
       no availability group, but it does have a card in manual mode and a hand-recorded claim can
       name it — reopening such a row used to select no card at all, and Apply then sent a null
       NDC alongside a perfectly good lot. */
    const seedOffered =
      !!seedNdc &&
      (groups.some((group) => group.ndc === seedNdc) ||
        ndcOptions.some((option) => option.ndc === seedNdc));

    if (!seedOffered) {
      setSelectedNdc(null);
      clearLotSelection();

      return;
    }

    const seedLotId = pendingLot?.inventoryLotId ?? soleRecordedLotId;
    const seedable = (usableByNdc.get(seedNdc) ?? []).some(
      (lot) => lot.inventoryLotId === seedLotId
    );

    setSelectedNdc(seedNdc);
    setSelectedLotId(seedable ? seedLotId : null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, item?.requestLineItemId]);

  // Earliest expiry first — the catalogue is already FEFO ordered, but the grid promises it.
  const orderedLots = useMemo(() => {
    const lots = selectedNdc ? (usableByNdc.get(selectedNdc) ?? []) : [];

    return [...lots].sort((a, b) => new Date(a.expirationDate) - new Date(b.expirationDate));
  }, [usableByNdc, selectedNdc]);

  /**
   * POM-6121 — is hand-recording available on this row at all?
   *
   * The POM-6083 switch, not a new one: the same env flag gates the same capability on the
   * single-item panel, and two flags for one feature is how half of it ends up live.
   *
   * Then core's own answer for this fill, and an inventory master to record against. With no
   * master NDC the panel could only ever refuse, so the affordance is not offered.
   */
  const canRecordManually =
    AddManualLotEnabled &&
    item?.canRecordUnverifiedLot === true &&
    !!recordable?.hasInventoryMaster &&
    ndcOptions.length > 0;

  /**
   * A fill is EITHER allocated from stock OR hand-recorded, never both.
   *
   * Same rule as the single-item panel, and for the same reason: mixing them would put one line
   * item's quantity across two buckets that are deliberately never summed, so nothing downstream
   * could say what was actually dispensed. Opening the panel IS the choice of mode, which is why
   * the stocked lot list is hidden for as long as it is open — leaving it visible but inert read
   * as "both are on offer", which is exactly what must not be possible.
   */
  const manualMode = manualOpen || !!manualSelection;

  // The cards, merged from the dispensing catalogue and the inventory-master NDC list. See
  // buildNdcCards for why neither list can carry the row on its own.
  const ndcCards = useMemo(
    () => buildNdcCards({ groups, ndcOptions, usableByNdc }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [groups, recordable, usableByNdc]
  );

  const manualEntry = deriveManualEntry({
    recordable,
    sessionLots: sessionManualLots,
    availabilityGroups: groups,
    ndc: selectedNdc,
    lotNumber: manualLotNumber,
    confirmLotNumber: manualConfirmLotNumber,
    bud: manualBud,
    pickedLotId: manualPickedLotId,
    fillQuantity,
    foreignNdcLots,
    // POM-6119 — the other drugs this batch already records this number against, worked out by
    // the review dialog, which owns the batch. The inventory lookup above cannot see them: when
    // both lots are new the collision exists only in the unsent batch.
    batchNdcsClaimingLot: (
      batchNdcClaims?.get(
        String(manualLotNumber ?? '')
          .trim()
          .toLowerCase()
      ) ?? []
    ).filter((other) => other !== selectedNdc),
    lookupFailed,
    sameNdcLotsOnFile,
    fulfillingPharmacyId: item?.fulfillingPharmacyId,
    todayStr,
  });

  const selectedStockLot = orderedLots.find((lot) => lot.inventoryLotId === selectedLotId) ?? null;

  /**
   * POM-6537 — the row's own lot, picked as it opened or picked again as a claim. It moves the row
   * nowhere, so it gives the other rows nothing to follow and "Also apply" is not offered.
   */
  const isOwnRecordedLot =
    !manualMode && !!selectedStockLot && selectedStockLot.inventoryLotId === soleRecordedLotId;

  /**
   * Is the current selection just what the row already records?
   *
   * Probed with the selection Apply would actually send, not a bare id: a short lot carries
   * `allowShortfallAsUnverified`, which is a real write against the very lot the row records, and
   * a bare id made that read as "nothing to change" — killing Apply on the case the claim exists
   * for. Same helper the review dialog stages through, so the button and the staging rule cannot
   * drift.
   */
  const isNoChange =
    !!selectedNdc &&
    !!selectedStockLot &&
    isSameAsRecorded({
      item,
      ndc: selectedNdc,
      lotAllocations: [buildStockLotSelection({ lot: selectedStockLot, fillQuantity })],
    });

  /**
   * A no-op has nothing to apply — UNLESS the row is carrying an uncommitted edit, in which case
   * applying the recorded values back IS the action: it takes that edit back. Disabling Apply
   * there would trap the user in their own change with only Cancel as a way out.
   *
   * POM-6121 — in manual mode Apply sends the hand-recorded claim, so it gates on that instead.
   *
   * Live off an un-added draft as well as an added one: the panel keeps Apply enabled the moment
   * the entry is valid, so filling the panel in and pressing Apply does the obvious thing rather
   * than silently requiring "Add lot" first.
   *
   * A hand-recorded claim is never a no-op, so there is no isNoChange half here — see
   * isSameAsRecorded.
   */
  // A draft the operator has started but not finished must not fall through to an earlier staged
  // claim: Apply would silently stage the old lot while the panel showed the new number, and the
  // blocking alert beside it would have nothing to do with what got applied.
  // While the panel is open, the draft is the only thing Apply may send. Falling back to an
  // already-staged claim here would apply a lot the panel is not showing — and emptying the lot
  // field is the easy way into that, since an empty draft is not "unfinished", it is absent.
  // Once the panel is closed the summary box IS on screen, so the staged claim is fair game.
  const canAddManualLot = manualEntry.canAdd && !lotCheckPending;

  // POM-6119 — shown, not gated. Stating the split is the screen's job; a tick per row is one
  // operators clear without reading, and it never reached the server. The review dialog carries
  // the batch total, and Commit is the gate.
  const showsShortfallNotice =
    !manualMode && shortfallAgainstFill({ lot: selectedStockLot, fillQuantity }) > 0;

  let canApply = !!selectedNdc && !!selectedLotId && (!isNoChange || !!change);
  if (manualMode) canApply = manualOpen ? canAddManualLot : !!manualSelection;

  /**
   * Picking an NDC.
   *
   * Switching NDC invalidates everything already entered about the lot, in both modes: a lot
   * number is only unique WITHIN an NDC, so carrying it across would claim a lot that does not
   * exist there, or create a bogus row for it. The BUD goes with it, having described the old lot.
   */
  const handleSelectNdc = (ndc) => {
    // Re-selecting the same NDC is not a change and must not wipe work in progress.
    if (ndc === selectedNdc) return;

    setSelectedNdc(ndc);
    clearLotSelection();
    clearManualDraft();
    setManualSelection(null);
  };

  /**
   * The NDC a claim is filed against.
   *
   * Moved to the product's default inventory-master NDC when whatever is selected is not one a
   * claim may be recorded against — the row may have opened on a stocked NDC inventory holds no
   * master row for, and the panel's first refusal would otherwise be about an NDC the operator
   * never chose.
   */
  const ensureRecordableNdc = (ndc) => {
    if (isRecordableNdc({ ndc, ndcOptions })) return;

    setSelectedNdc((ndcOptions.find((option) => option.isDefault) ?? ndcOptions[0]).ndc);
  };

  /**
   * Starting a NEW hand-recorded entry.
   *
   * Drops anything picked from stock, because a fill is one or the other and the operator has just
   * said which. Answering the request is better than an error telling them to go and undo it.
   */
  const startManualEntry = () => {
    if (!canRecordManually) return;

    clearLotSelection();
    clearManualDraft();
    ensureRecordableNdc(selectedNdc);
    setManualOpen(true);
  };

  /**
   * Reopening the claim that is already staged, to edit it.
   *
   * Seeded from the staged selection rather than opened blank. Blank looked like nothing was
   * staged while the claim was still live underneath: Apply then re-sent the old entry and Cancel
   * destroyed it, both without showing the operator what they were acting on.
   *
   * The re-key is seeded too, so an entry already confirmed once is not re-typed to fix a BUD.
   * Changing the lot number makes the two diverge again, which is exactly when the re-key should
   * come back.
   */
  const editManualSelection = () => {
    if (!canRecordManually || !manualSelection) return;

    clearLotSelection();
    setManualLotNumber(manualSelection.lotNumber ?? '');
    setManualConfirmLotNumber(manualSelection.lotNumber ?? '');
    setManualBud(manualSelection.expirationDate ?? '');
    setManualPickedLotId(manualSelection.inventoryLotId ?? null);
    ensureRecordableNdc(manualSelection.ndc ?? selectedNdc);
    setManualOpen(true);
  };

  /**
   * Closing the panel without deciding anything — the X and Cancel.
   *
   * Abandons the DRAFT only. A claim staged before the panel was reopened survives, because
   * cancelling an edit means "leave it as it was", not "throw it away"; throwing it away is what
   * Remove is for.
   */
  const dismissManualEntry = () => {
    setManualOpen(false);
    clearManualDraft();
  };

  /** Deliberately discarding the claim — Remove, and picking a lot from stock instead. */
  const removeManualSelection = () => {
    setManualOpen(false);
    clearManualDraft();
    setManualSelection(null);
  };

  /**
   * Picking a lot from stock.
   *
   * Clears any hand-recorded claim, for the same reason opening the panel clears the stock pick:
   * one line item's quantity cannot sit in both buckets.
   */
  const handleSelectStockLot = (inventoryLotId) => {
    setSelectedLotId(inventoryLotId);
    removeManualSelection();
  };

  /**
   * Picking, or typing, a lot inside the panel.
   *
   * Picking an existing row adopts its identity wholesale: its lot number and its BUD. Letting the
   * operator then edit that BUD would describe a different lot than the one being claimed.
   *
   * A string arrives from the free-text side of the Autocomplete, including on every autoSelect
   * blur — so the BUD is dropped only when it was ADOPTED from a lot that has now been abandoned.
   * A BUD the operator typed themselves is theirs to keep.
   */
  const handlePickRecordableLot = (value) => {
    if (typeof value === 'string' || value === null) {
      setManualLotNumber(typeof value === 'string' ? value : '');
      if (manualPickedLotId) setManualBud('');
      setManualPickedLotId(null);

      return;
    }

    setManualLotNumber(value.lotNumber);
    // The re-key answers for one exact value, so a pick that replaces it invalidates it. The string
    // branch above deliberately does NOT clear it: that fires on every autoSelect blur, including
    // one that changed nothing, and a correct re-key must survive tabbing back through.
    setManualConfirmLotNumber('');
    // Null for a lot typed against another row of this batch: it has no inventory id yet, and the
    // typed-number match is what resolves it back to the same entry.
    setManualPickedLotId(value.inventoryLotId ?? null);

    // A dated lot dictates the field. An undated one leaves whatever has been typed, so reading
    // the BUD off the vial before picking the lot from the list is not punished.
    if (value.expirationDate) setManualBud(String(value.expirationDate).slice(0, 10));
  };

  const handleAddManualLot = () => {
    // Pending is checked here as well as on the button, matching the single-item panel. Nothing
    // reaches this today except the button, but a selection is non-null while a check is still
    // outstanding, so the handler on its own would stage a lot nothing has verified.
    if (!manualEntry.selection || lotCheckPending) return;

    setManualSelection(manualEntry.selection);
    setManualOpen(false);
  };

  /**
   * What Apply would send right now, or null. One answer for Apply and for the Before → After
   * strip, so the strip can never describe a different pick than the one applied.
   */
  const pendingApplySelection = () => {
    if (manualMode) {
      // The live draft wins: re-opening the panel and typing a new lot is the operator replacing
      // what they staged, so preferring the staged one would discard the entry they just made.
      const selection = manualEntry.selection ?? manualSelection;
      if (!selection) return null;

      // The selection's own NDC, not the live card state: that is the NDC the entry was validated
      // against, and the two can only differ through a code path that should not exist.
      return { ndc: selection.ndc, lotAllocations: [selection], stockLot: null };
    }

    const lot = orderedLots.find((candidate) => candidate.inventoryLotId === selectedLotId);
    if (!lot) return null;

    return {
      ndc: selectedNdc,
      lotAllocations: [buildStockLotSelection({ lot, fillQuantity })],
      stockLot: lot,
    };
  };

  // POM-6537 — the same pick for the other rows of this NDC. A draft still being checked is not a
  // pick yet, and the row's own lot is no move to follow.
  const bulkApply = useBulkApplyChoice({
    open,
    item,
    selectedNdc,
    selection:
      (manualOpen && !canAddManualLot) || isOwnRecordedLot ? null : pendingApplySelection(),
    planBulkApplyFor,
  });
  const appliedRowCount = 1 + bulkApply.bulkChanges.length;

  const handleApply = () => {
    const selection = pendingApplySelection();
    if (!selection) return;

    onApply({
      requestLineItemId: item.requestLineItemId,
      ndc: selection.ndc,
      lotAllocations: selection.lotAllocations,
      ...(bulkApply.bulkChanges.length > 0 ? { bulkChanges: bulkApply.bulkChanges } : {}),
    });
  };

  const unusableRecordedLot = findLotNeedingReplacement({
    recordedLots: item?.recordedLots,
    fillQuantity,
    todayStr,
    shortfallAllowed,
  });

  if (!item) return null;

  const applyPreview = canApply ? pendingApplySelection() : null;

  return (
    // Escape cancels; a backdrop click does not, so a mis-click cannot discard a half-made pick.
    // Held at the top rather than centred: centred, the whole dialog moved up and down every time
    // the lot list changed length, so the NDC cards slid away under the pointer (QA, 2 Nov).
    <Dialog
      open={open}
      onClose={(event, reason) => reason !== 'backdropClick' && onCancel()}
      maxWidth={false}
      fullWidth
      PaperProps={{ sx: { maxWidth: 760 } }}
      sx={{ '& .MuiDialog-container': { alignItems: 'flex-start' } }}
    >
      <ChangeNdcLotDialogTitle
        item={item}
        fillQuantity={fillQuantity}
        unusableRecordedLot={unusableRecordedLot}
        batchFreeQty={batchLotShare?.get(unusableRecordedLot?.inventoryLotId)?.freeQty ?? null}
        shortfallAllowed={shortfallAllowed}
        todayStr={todayStr}
        onClose={onCancel}
      />

      {/* dividers: a rule under the heading and above the footer, so the three parts read apart. */}
      <DialogContent dividers>
        <ChangeNdcLotNdcCards
          ndcCards={ndcCards}
          ndcOptions={ndcOptions}
          selectedNdc={selectedNdc}
          preferredNdc={preferredNdc}
          manualMode={manualMode}
          facilityName={item.fulfillingPharmacyName}
          onSelectNdc={handleSelectNdc}
        />

        <Divider sx={{ my: 2 }} />

        <ChangeNdcLotLotSection
          caption={
            selectedNdc && !manualMode ? `for NDC ${selectedNdc} · earliest expiry first` : ''
          }
          headerAction={
            /* POM-6121 - the way in. Offered whenever hand-recording is available on the row
               rather than only when the picker is empty: an operator can discover the lot on
               offer is not the lot in their hand at any point. */
            canRecordManually &&
            !manualMode && (
              <Button
                size='small'
                variant='outlined'
                startIcon={<AddIcon />}
                onClick={startManualEntry}
                sx={{
                  color: warningTextTone,
                  borderColor: (theme) => warningTint(theme, 0.6),
                  whiteSpace: 'nowrap',
                }}
              >
                Enter lot manually
              </Button>
            )
          }
        >
          {/* Hidden for as long as the panel is open or a hand-recorded claim is staged:
              opening the panel IS the choice of mode, and leaving the stock list visible
              but inert read as "both are on offer". */}
          {!manualMode && (
            <ChangeNdcLotStockLots
              lots={orderedLots}
              selectedNdc={selectedNdc}
              selectedLotId={selectedLotId}
              recordedLotIds={recordedLotIds}
              fillQuantity={item.fillQuantity}
              shortfallAllowed={shortfallAllowed}
              facilityName={item.fulfillingPharmacyName}
              onSelectLot={handleSelectStockLot}
            />
          )}

          {manualOpen && (
            <ChangeNdcLotManualEntry
              derived={manualEntry}
              fillQuantity={item.fillQuantity}
              lotNumber={manualLotNumber}
              confirmLotNumber={manualConfirmLotNumber}
              onLotNumberChange={(value) => {
                setManualLotNumber(value);
                setManualPickedLotId(null);
              }}
              onConfirmLotNumberChange={setManualConfirmLotNumber}
              onPickLot={handlePickRecordableLot}
              onBudChange={setManualBud}
              onAddLot={handleAddManualLot}
              onClose={dismissManualEntry}
              isCheckingLotNumber={lotCheckPending}
            />
          )}

          {!manualOpen && manualSelection && (
            <ChangeNdcLotManualSummary
              selection={manualSelection}
              onChange={editManualSelection}
              onRemove={removeManualSelection}
            />
          )}
        </ChangeNdcLotLotSection>

        {showsShortfallNotice && (
          <ChangeNdcLotShortfallNotice
            lot={selectedStockLot}
            fillQuantity={fillQuantity}
            rxNumber={item.rxNumber}
            keepsLabel={
              selectedNdc === item.effectiveNdc &&
              selectedStockLot?.inventoryLotId === soleRecordedLotId
            }
          />
        )}

        {/* Only for a real move with somewhere to go: a disabled offer the operator cannot act on
            from here only raised the question of why (QA, 2 Nov). */}
        {bulkApply.isAvailable && bulkApply.otherRowCount > 0 && (
          <ChangeNdcLotBulkApply bulkApply={bulkApply} ndc={item.effectiveNdc} />
        )}
      </DialogContent>

      {/* POM-6534: nothing is said while Apply is disabled; the strip appears with something to
          apply. */}
      <DialogActions sx={{ px: 3, py: 1.5 }}>
        {applyPreview && (
          <ChangeNdcLotBeforeAfter
            before={describeRecordedLabel({ item, todayStr })}
            after={describeLabelAfterApply({
              ndc: applyPreview.ndc,
              allocation: applyPreview.lotAllocations[0],
              stockLot: applyPreview.stockLot,
              fillQuantity,
            })}
          />
        )}
        <Button onClick={onCancel}>Cancel</Button>
        <Button variant='contained' onClick={handleApply} disabled={!canApply}>
          {appliedRowCount > 1 ? `Apply to ${appliedRowCount} rows` : 'Apply'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

ChangeNdcLotDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  item: PropTypes.shape({
    requestLineItemId: PropTypes.string,
    orderId: PropTypes.string,
    rxNumber: PropTypes.string,
    medicationName: PropTypes.string,
    strength: PropTypes.string,
    fillQuantity: PropTypes.string,
    fulfillingPharmacyName: PropTypes.string,
    /** POM-6121 - which site's row decides whether this lot number is already expired on file. */
    fulfillingPharmacyId: PropTypes.string,
    effectiveNdc: PropTypes.string,
    partnerPreferredNdc: PropTypes.string,
    recordedLots: PropTypes.arrayOf(PropTypes.shape({})),
    /** POM-6121 - core's answer on whether this fill may carry a hand-recorded claim. */
    canRecordUnverifiedLot: PropTypes.bool,
  }),
  availability: PropTypes.shape({
    groups: PropTypes.arrayOf(PropTypes.shape({})),
  }),
  /**
   * POM-6121 - what a lot may be hand-recorded against for this product and facility, from the
   * same review payload as `availability`. Null when nothing may be: the affordance is then not
   * offered at all rather than offered and refused.
   */
  recordable: PropTypes.shape({
    hasInventoryMaster: PropTypes.bool,
    ndcOptions: PropTypes.arrayOf(PropTypes.shape({})),
    depletedLots: PropTypes.arrayOf(PropTypes.shape({})),
  }),
  /**
   * POM-6121 - lots already typed for OTHER rows of this batch, on the same product and facility.
   * Offered alongside the product's own zero-quantity lots so one lot number entered once covers
   * every row it applies to - retyping it is how two claims get posted for one physical lot.
   */
  sessionManualLots: PropTypes.arrayOf(PropTypes.shape({})),
  /** Lot number (lowercased) -> NDCs elsewhere in this batch already recording it. */
  batchNdcClaims: PropTypes.instanceOf(Map),
  /** POM-6536 — item -> Map of lot id -> what the rest of the batch leaves on it. */
  batchLotShareFor: PropTypes.func,
  /** POM-6537 — the other rows this pick can also go to; see bulkApplyPlan. */
  planBulkApplyFor: PropTypes.func,
  /** Cross-NDC lookups already answered in this sitting. Owned by the review dialog. */
  // eslint-disable-next-line react/forbid-prop-types
  lotLookupCache: PropTypes.object,
  /**
   * This row's uncommitted edit, if it already has one. Seeds the picker so a reopened row shows
   * the pick the user made rather than the record it is about to replace, and keeps Apply live so
   * they can put the recorded lot back.
   */
  change: PropTypes.shape({
    ndc: PropTypes.string,
    lotAllocations: PropTypes.arrayOf(PropTypes.shape({})),
  }),
  todayStr: PropTypes.string.isRequired,
  onApply: PropTypes.func.isRequired,
  onCancel: PropTypes.func.isRequired,
};

ChangeNdcLotDialog.defaultProps = {
  item: null,
  availability: null,
  recordable: null,
  sessionManualLots: [],
  batchNdcClaims: new Map(),
  batchLotShareFor: null,
  planBulkApplyFor: null,
  lotLookupCache: null,
  change: null,
};

export default ChangeNdcLotDialog;
